/**
 * MedjimurjeMap — interactive SVG map of Međimurje county.
 *
 * Three colored regions (Gornje=green, Srednje=amber, Donje=blue) with
 * refined geometry inspired by the real county silhouette. 23 pins for
 * cities/villages drive per-place news filtering.
 *
 * Additionally, 4 transparent "external" regions are drawn in the canvas
 * corners (Švicarska top-left, Svijet top-right, Varaždin bottom-left,
 * Hrvatska bottom-right). They are invisible but HOVER-highlight with a
 * dashed outline and emit `onSelectExternal(key)` on click so the parent
 * can navigate to a tag/search filter.
 *
 * Props:
 *   placeCounts       : { [placeName]: number }  — article counts per place
 *   activePlace       : string | null            — currently-selected pin
 *   activeExternal    : string | null            — currently-selected corner
 *   onSelect          : (placeName) => void
 *   onSelectRegion    : (regionKey) => void
 *   onSelectExternal  : ('svicarska'|'svijet'|'varazdin'|'hrvatska') => void
 *   showLabels        : boolean (default true)
 */

// ─────────────────────────── REFINED REGION PATHS ───────────────────────────
// viewBox = 1000 x 560. Paths attempt to match the real Međimurje silhouette
// with a north-leaning Gornje band (green), a central Srednje band (amber)
// descending from Ormož in the west to Podturen in the east, and a large
// Donje Međimurje spread across the east/south down to Donja Dubrava.
// Coordinates were traced off the attached reference photo.

const REGIONS = [
  {
    key: 'gornje',
    label: 'Gornje Međimurje',
    fill: '#86efac',
    strokeFill: '#16a34a',
    path: 'M 150 170 L 220 130 L 300 110 L 360 80 L 420 120 L 470 140 L 520 100 L 570 80 L 640 120 L 690 160 L 720 220 L 700 260 L 650 280 L 590 270 L 520 290 L 440 280 L 360 300 L 290 280 L 220 290 L 170 250 Z',
    places: [
      { name: 'Štrigova', x: 250, y: 175 },
      { name: 'Sv. Martin na Muri', x: 430, y: 135 },
      { name: 'Mursko Središće', x: 570, y: 155 },
      { name: 'Železna Gora', x: 280, y: 230 },
      { name: 'Stanetinec', x: 210, y: 255 },
      { name: 'Vratišinec', x: 500, y: 220 },
      { name: 'Macinec', x: 260, y: 295 },
    ],
  },
  {
    key: 'srednje',
    label: 'Srednje Međimurje',
    fill: '#fcd34d',
    strokeFill: '#d97706',
    path: 'M 170 250 L 220 290 L 290 280 L 360 300 L 440 280 L 520 290 L 560 360 L 540 450 L 490 490 L 410 510 L 340 490 L 280 440 L 220 380 L 180 320 Z',
    places: [
      { name: 'Lopatinec', x: 380, y: 350 },
      { name: 'Čakovec', x: 450, y: 430 },
      { name: 'Nedelišće', x: 350, y: 470 },
      { name: 'Kuršanec', x: 420, y: 505 },
    ],
  },
  {
    key: 'donje',
    label: 'Donje Međimurje',
    fill: '#93c5fd',
    strokeFill: '#2563eb',
    path: 'M 560 360 L 590 270 L 650 280 L 700 260 L 770 290 L 830 320 L 880 360 L 910 420 L 880 470 L 810 500 L 730 505 L 640 510 L 560 490 L 540 450 L 540 410 Z',
    places: [
      { name: 'Podturen', x: 620, y: 290 },
      { name: 'Domašinec', x: 720, y: 340 },
      { name: 'Belica', x: 580, y: 400 },
      { name: 'Mala Subotica', x: 530, y: 445 },
      { name: 'Sveti Juraj u Trnju', x: 680, y: 460 },
      { name: 'Goričan', x: 780, y: 370 },
      { name: 'Serdahelj', x: 850, y: 350 },
      { name: 'Kotoriba', x: 870, y: 410 },
      { name: 'Prelog', x: 640, y: 490 },
      { name: 'Orehovica', x: 560, y: 495 },
      { name: 'Dražkovec', x: 750, y: 485 },
      { name: 'Donja Dubrava', x: 870, y: 480 },
    ],
  },
];

// ─────────────────────────── EXTERNAL CORNERS ───────────────────────────
// These 4 areas are NOT part of Međimurje — they are contextual entry points
// to adjacent/world news so visitors can jump from the local map to a broader
// news context.
const EXTERNAL = [
  // top-left
  { key: 'svicarska', label: 'Švicarska', emoji: '🇨🇭',
    rect: { x: 10, y: 10, w: 150, h: 90 }, anchor: { x: 85, y: 55 } },
  // top-right
  { key: 'svijet', label: 'Svijet', emoji: '🌍',
    rect: { x: 840, y: 10, w: 150, h: 90 }, anchor: { x: 915, y: 55 } },
  // bottom-left
  { key: 'varazdin', label: 'Varaždin', emoji: '🏰',
    rect: { x: 10, y: 460, w: 150, h: 90 }, anchor: { x: 85, y: 505 } },
  // bottom-right
  { key: 'hrvatska', label: 'Hrvatska', emoji: '🇭🇷',
    rect: { x: 840, y: 460, w: 150, h: 90 }, anchor: { x: 915, y: 505 } },
];

export default function MedjimurjeMap({
  placeCounts = {},
  activePlace = null,
  activeExternal = null,
  onSelect = () => {},
  onSelectRegion = () => {},
  onSelectExternal = () => {},
  showLabels = true,
}) {
  return (
    <svg viewBox="0 0 1000 560" className="w-full h-auto block" role="img"
         aria-label="Karta Međimurja" data-testid="medjimurje-svg">
      {/* ── Corner "external" clickable areas (rendered UNDER regions) ── */}
      {EXTERNAL.map((ex) => {
        const active = activeExternal === ex.key;
        return (
          <g key={ex.key} className="cursor-pointer group"
             onClick={(e) => { e.stopPropagation(); onSelectExternal(ex.key); }}
             data-testid={`external-${ex.key}`}>
            <rect
              x={ex.rect.x} y={ex.rect.y} width={ex.rect.w} height={ex.rect.h}
              rx={14} ry={14}
              fill={active ? '#dc2626' : '#0f172a'}
              fillOpacity={active ? 0.95 : 0.78}
              stroke={active ? '#fca5a5' : '#475569'}
              strokeWidth={1.75}
              className="transition-all group-hover:fill-red-700 group-hover:stroke-red-300"
            />
            <text x={ex.anchor.x} y={ex.anchor.y - 6} textAnchor="middle"
                  fontSize="22" className="pointer-events-none select-none">
              {ex.emoji}
            </text>
            <text x={ex.anchor.x} y={ex.anchor.y + 22} textAnchor="middle"
                  fontSize="14" fontWeight="800"
                  fill="#ffffff" letterSpacing="0.4"
                  className="pointer-events-none select-none">
              {ex.label}
            </text>
          </g>
        );
      })}

      {/* ── Main Međimurje regions ── */}
      {REGIONS.map((r) => (
        <g key={r.key} className="cursor-pointer"
           onClick={() => onSelectRegion(r.key)}
           data-testid={`region-${r.key}`}>
          <path d={r.path} fill={r.fill} stroke={r.strokeFill} strokeWidth={1.75}
                className="transition-opacity hover:opacity-90" />
        </g>
      ))}

      {/* ── Pins ── */}
      {REGIONS.flatMap((r) => r.places.map((p) => {
        const active = activePlace === p.name;
        const count = placeCounts[p.name] || 0;
        return (
          <g key={p.name} className="cursor-pointer"
             onClick={(e) => { e.stopPropagation(); onSelect(p.name); }}
             data-testid={`pin-${p.name}`}>
            {active && <circle cx={p.x} cy={p.y} r={16} fill="rgba(220,38,38,0.3)" />}
            <circle cx={p.x} cy={p.y} r={active ? 7 : 5.5}
                    fill={active ? '#dc2626' : '#0f172a'}
                    stroke="#fff" strokeWidth={2}
                    className="transition-[r,fill] duration-150 hover:fill-red-600" />
            {count > 0 && (
              <g>
                <circle cx={p.x + 10} cy={p.y - 10} r={8} fill="#dc2626" />
                <text x={p.x + 10} y={p.y - 7} textAnchor="middle" fontSize="9"
                      fontWeight="700" fill="#fff" className="pointer-events-none">
                  {count > 99 ? '99+' : count}
                </text>
              </g>
            )}
            {showLabels && (
              <text x={p.x} y={p.y + 18} textAnchor="middle" fontSize="11"
                    fontWeight="600" fill="#0f172a"
                    className="pointer-events-none select-none"
                    style={{ paintOrder: 'stroke', stroke: '#ffffff',
                             strokeWidth: 3, strokeLinejoin: 'round' }}>
                {p.name}
              </text>
            )}
          </g>
        );
      }))}
    </svg>
  );
}

export const MEDJIMURJE_REGIONS = REGIONS;
export const MEDJIMURJE_PLACES = REGIONS.flatMap((r) => r.places.map((p) => ({ ...p, region: r.key })));
export const MEDJIMURJE_EXTERNAL = EXTERNAL;
